import { Component, Input, ChangeDetectionStrategy, OnInit, OnChanges, SimpleChanges, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DateAdapter, MAT_DATE_FORMATS, MatNativeDateModule } from '@angular/material/core';
import { SchoolData, SchoolStatusOption, SchoolTypeOption, SeverityLevel, ToastMessageType } from '@k12/models';
import { SchoolsService } from '@k12/app-services';
import { AppInsightsService } from '@k12/error_handler';
import { ToastMessageService } from '@k12/notifications';
import { MonthDayDateAdapter, MONTH_DAY_FORMAT } from '../../../date-adapters/month-day-date-adapter';
import { provideNgxMask } from 'ngx-mask';
import { FormInlineErrorComponent } from '@k12/forms';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,

  selector: 'lib-school-details',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatTooltipModule,
    FormInlineErrorComponent
  ],
  providers: [
    provideNgxMask(),
    { provide: DateAdapter, useClass: MonthDayDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MONTH_DAY_FORMAT }
  ],
  templateUrl: './school-details.component.html'
})
export class SchoolDetailsComponent implements OnInit, OnChanges {
  @Input() formGroup!: FormGroup;
  @Input() schoolData!: SchoolData;
  @Input() aidProgramOptions: string[] = [];
  @Input() institutionSchoolCount: number = 1;

  schoolStatusOptions: SchoolStatusOption[] = [];
  schoolTypeOptions: SchoolTypeOption[] = [];

  educationDeliveryMethods = [
    { id: 1, label: 'Remote/Virtual' },
    { id: 2, label: 'In Person' }
  ];

  private readonly schoolsService = inject(SchoolsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly appInsightsService = inject(AppInsightsService);
  private readonly toastMessageService = inject(ToastMessageService);

  ngOnInit(): void {
    this.updateFiscalYearEndDateState();
    this.updateFeinState();
    this.loadSchoolStatuses();
    this.loadSchoolTypes();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['institutionSchoolCount'] || changes['formGroup']) {
      this.updateFiscalYearEndDateState();
      this.updateFeinState();
    }
  }

  private loadSchoolStatuses(): void {
    this.schoolsService
      .getSchoolStatusesForDropdown()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.schoolStatusOptions = response?.entity ?? [];
        },
        error: (error: unknown) => {
          this.schoolStatusOptions = [];
          const err = error instanceof Error ? error : new Error(String(error));
          this.appInsightsService.log(err, SeverityLevel.error);
          this.toastMessageService.showToast(
            ToastMessageType.Error,
            'Failed to load school statuses. Status cannot be changed until this is resolved.'
          );
        }
      });
  }

  private loadSchoolTypes(): void {
    this.schoolsService
      .getSchoolTypesForDropdown('', true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.schoolTypeOptions = response?.entity ?? [];
        },
        error: (error: unknown) => {
          this.schoolTypeOptions = [];
          const err = error instanceof Error ? error : new Error(String(error));
          this.appInsightsService.log(err, SeverityLevel.error);
          this.toastMessageService.showToast(
            ToastMessageType.Error,
            'Failed to load school types. School Type cannot be changed until this is resolved.'
          );
        }
      });
  }

  private updateFiscalYearEndDateState(): void {
    const control = this.formGroup?.get('fiscalYearEndDate');
    if (control) {
      if (this.institutionHasMoreThanOneSchool) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    }
  }

  private updateFeinState(): void {
    const control = this.formGroup?.get('feinNumber');
    if (control) {
      if (this.institutionHasMoreThanOneSchool) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    }
  }

  get institutionHasMoreThanOneSchool(): boolean {
    return this.institutionSchoolCount > 1;
  }

  get moreThanOneSchoolTooltip(): string {
    return this.institutionHasMoreThanOneSchool
      ? 'This school belongs to an Institution with more than one school. Please edit at the institution level.'
      : '';
  }

}
